/**
 * Todo Service
 * 
 * API service for HMS To-Do List
 * Handles all todo-related API calls
 * 
 * Endpoints:
 * - GET    /api/todos/                  - List todos
 * - POST   /api/todos/                  - Create todo
 * - GET    /api/todos/{id}/             - Get specific todo
 * - PATCH  /api/todos/{id}/             - Update todo
 * - DELETE /api/todos/{id}/             - Delete todo
 * - POST   /api/todos/{id}/mark_complete/ - Mark as complete
 * - POST   /api/todos/{id}/mark_in_progress/ - Mark as in progress
 * - GET    /api/todos/stats/            - Get statistics
 * - GET    /api/todos/categories/       - Get user's categories
 */

import axiosInstance from '../utils/axiosConfig';

const todoService = {
  /**
   * Get list of todos with optional filters
   * 
   * @param {Object} filters - Filter parameters
   * @param {string} filters.status - Filter by status (pending, in_progress, completed, cancelled)
   * @param {string} filters.priority - Filter by priority (low, normal, high)
   * @param {string} filters.category - Filter by category
   * @param {boolean} filters.overdue - Filter overdue todos
   * @returns {Promise<Array>} List of todos
   */
  async getTodos(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.priority && filters.priority !== 'all') {
        params.append('priority', filters.priority);
      }
      if (filters.category) {
        params.append('category', filters.category);
      }
      if (filters.overdue === true) {
        params.append('overdue', 'true');
      }

      const queryString = params.toString();
      const url = queryString ? `/api/todos/?${queryString}` : '/api/todos/';
      
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching todos:', error);
      throw error;
    }
  },

  /**
   * Get a specific todo by ID
   * 
   * @param {number} id - Todo ID
   * @returns {Promise<Object>} Todo object
   */
  async getTodoById(id) {
    try {
      const response = await axiosInstance.get(`/api/todos/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching todo ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new todo
   * 
   * @param {Object} data - Todo data
   * @param {string} data.title - Todo title (required)
   * @param {string} data.description - Todo description (optional)
   * @param {string} data.priority - Priority: low, normal, high (default: normal)
   * @param {string} data.category - Category (optional)
   * @param {string} data.due_date - Due date in YYYY-MM-DD format (optional)
   * @returns {Promise<Object>} Created todo
   */
  async createTodo(data) {
    try {
      const response = await axiosInstance.post('/api/todos/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating todo:', error);
      throw error;
    }
  },

  /**
   * Update a todo
   * 
   * @param {number} id - Todo ID
   * @param {Object} data - Update data
   * @returns {Promise<Object>} Updated todo
   */
  async updateTodo(id, data) {
    try {
      const response = await axiosInstance.patch(`/api/todos/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating todo ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a todo
   * 
   * @param {number} id - Todo ID
   * @returns {Promise<void>}
   */
  async deleteTodo(id) {
    try {
      await axiosInstance.delete(`/api/todos/${id}/`);
    } catch (error) {
      console.error(`Error deleting todo ${id}:`, error);
      throw error;
    }
  },

  /**
   * Mark a todo as complete
   * 
   * @param {number} id - Todo ID
   * @returns {Promise<Object>} Updated todo
   */
  async markComplete(id) {
    try {
      const response = await axiosInstance.post(`/api/todos/${id}/mark_complete/`);
      return response.data;
    } catch (error) {
      console.error(`Error marking todo ${id} as complete:`, error);
      throw error;
    }
  },

  /**
   * Mark a todo as in progress
   * 
   * @param {number} id - Todo ID
   * @returns {Promise<Object>} Updated todo
   */
  async markInProgress(id) {
    try {
      const response = await axiosInstance.post(`/api/todos/${id}/mark_in_progress/`);
      return response.data;
    } catch (error) {
      console.error(`Error marking todo ${id} as in progress:`, error);
      throw error;
    }
  },

  /**
   * Get todo statistics
   * 
   * @returns {Promise<Object>} Statistics object
   */
  async getStats() {
    try {
      const response = await axiosInstance.get('/api/todos/stats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching todo stats:', error);
      throw error;
    }
  },

  /**
   * Get list of user's todo categories
   * 
   * @returns {Promise<Array>} List of category names
   */
  async getCategories() {
    try {
      const response = await axiosInstance.get('/api/todos/categories/');
      return response.data.categories;
    } catch (error) {
      console.error('Error fetching todo categories:', error);
      throw error;
    }
  },

  /**
   * Toggle todo completion status
   * Helper method to toggle between completed and pending
   * 
   * @param {number} id - Todo ID
   * @param {string} currentStatus - Current status
   * @returns {Promise<Object>} Updated todo
   */
  async toggleComplete(id, currentStatus) {
    try {
      if (currentStatus === 'completed') {
        // Revert to pending
        return await this.updateTodo(id, { status: 'pending' });
      } else {
        // Mark as complete
        return await this.markComplete(id);
      }
    } catch (error) {
      console.error(`Error toggling todo ${id} completion:`, error);
      throw error;
    }
  }
};

export default todoService;
